// AB Soft Import wizard — upload IA1 / IA31 / IA43 / IA42 CSV exports
// from AB Soft, preview matched/new rows, confirm to upsert.
//
// Backend: POST /api/import/{kind}/{companyId}?preview={true|false} with multipart file.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Upload,
  Button,
  Tabs,
  Tag,
  message,
  Spin,
  Table,
  Alert,
  DatePicker,
  Select,
} from 'antd';
import * as ozService from '@/services/payrollEngine.service';

const KINDS = [
  { key: 'ia1', tKey: 'ozImportIa1' },
  { key: 'ia31', tKey: 'ozImportIa31' },
  { key: 'ia43', tKey: 'ozImportIa43' },
  { key: 'ia43-targets', tKey: 'ozImportIa43Targets' },
  { key: 'ia42', tKey: 'ozImportIa42' },
  { key: 'long-sick', tKey: 'ozImportLongSick' },
];

function ImportPanel({ companyId, kind, t }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committed, setCommitted] = useState(null);
  const [godMes, setGodMes] = useState(null);
  // Which AB Obracun=09 code the file is read for (122 long sick / 213 dopuna).
  const [absCode, setAbsCode] = useState(['122']);

  const needsGodMes = kind === 'ia43-targets';
  const needsCode = kind === 'long-sick';
  const extraParams = () => {
    if (needsGodMes) return godMes ? { godMes: godMes.format('YYYYMM') } : {};
    if (needsCode) {
      return { code: Array.isArray(absCode) ? absCode.join(',') : absCode };
    }
    return {};
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setCommitted(null);
  };

  const handleUpload = async (f) => {
    if (needsGodMes && !godMes) {
      message.warning(t('ozImportTargetMonthRequired'));
      return false;
    }
    setFile(f);
    setPreview(null);
    setCommitted(null);
    setBusy(true);
    try {
      const data = await ozService.importAbsoftCsv({
        companyId,
        kind,
        file: f,
        preview: true,
        params: extraParams(),
      });
      setPreview(data);
    } catch (err) {
      message.error(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
    return false; // prevent default upload
  };

  const handleCommit = async () => {
    if (!file) return;
    if (needsGodMes && !godMes) {
      message.warning(t('ozImportTargetMonthRequired'));
      return;
    }
    setBusy(true);
    try {
      const data = await ozService.importAbsoftCsv({
        companyId,
        kind,
        file,
        preview: false,
        params: extraParams(),
      });
      setCommitted(data);
      message.success(t('ozImportDone'));
      queryClient.invalidateQueries({ queryKey: ['oz-company-config'] });
      queryClient.invalidateQueries({ queryKey: ['oz-employee-profiles'] });
    } catch (err) {
      message.error(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card size="small" className="mb-4">
      <div className="mb-2 text-xs text-gray-500">
        {t('ozImportFormatHint')}
      </div>
      <div className="mb-4 flex items-center gap-2">
        {needsGodMes && (
          <DatePicker
            picker="month"
            size="small"
            format="YYYYMM"
            placeholder={t('ozImportTargetMonth')}
            value={godMes}
            onChange={setGodMes}
            disabled={busy}
          />
        )}
        {needsCode && (
          <Select
            size="small"
            style={{ minWidth: 320 }}
            value={absCode}
            onChange={setAbsCode}
            disabled={busy}
            mode="tags"
            tokenSeparators={[',', ' ']}
            placeholder={t('ozImportCodesPlaceholder')}
            options={[
              { value: '122', label: t('ozImportCode122') },
              { value: '213', label: t('ozImportCode213') },
              { value: '147', label: t('ozImportCode147') },
              { value: '121', label: t('ozImportCode121') },
              { value: '130', label: t('ozImportCode130') },
              { value: '227', label: t('ozImportCode227') },
              { value: '228', label: t('ozImportCode228') },
            ]}
          />
        )}
        <Upload
          accept=".csv"
          maxCount={1}
          showUploadList={false}
          beforeUpload={handleUpload}
          disabled={busy}
        >
          <Button icon={<UploadOutlined />} loading={busy} disabled={busy}>
            {t('ozImportUpload')}
          </Button>
        </Upload>
        {file ? (
          <span className="text-xs text-gray-500">
            {file.name} ({Math.round(file.size / 1024)} KB)
          </span>
        ) : null}
        {(preview || committed) && (
          <Button size="small" onClick={reset}>
            {t('reset')}
          </Button>
        )}
      </div>

      {busy && !preview && !committed && <Spin />}

      {preview && !committed && (
        <>
          <div className="mb-3 flex items-center gap-3">
            <Tag color="blue">
              {t('ozImportRows')}: {preview.total}
            </Tag>
            <Tag color="green">
              {t('ozImportMatched')}: {preview.matched ?? 0}
            </Tag>
            <Tag color="orange">
              {t('ozImportNew')}: {preview.new ?? 0}
            </Tag>
          </div>
          {preview.sample?.length > 0 && (
            <PreviewTable kind={kind} sample={preview.sample} />
          )}
          <div className="mt-3 flex justify-end">
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={busy}
              onClick={handleCommit}
            >
              {t('ozImportConfirm')}
            </Button>
          </div>
        </>
      )}

      {committed && (
        <Alert
          type="success"
          message={t('ozImportDone')}
          description={
            <ul className="ml-4 list-disc text-xs">
              <li>rows: {committed.rows ?? committed.upserted ?? '-'}</li>
              {committed.unmatched != null && (
                <li>unmatched: {committed.unmatched}</li>
              )}
            </ul>
          }
        />
      )}
    </Card>
  );
}

function PreviewTable({ kind, sample }) {
  const { t } = useTranslation(['forms']);
  if (kind === 'ia1') {
    return (
      <Table
        size="small"
        rowKey={(_, i) => i}
        pagination={false}
        dataSource={sample}
        columns={[
          { title: t('code'), dataIndex: 'code', width: 90 },
          { title: t('name'), dataIndex: 'naziv' },
          { title: t('ozObracun'), dataIndex: 'obracun', width: 90 },
          { title: t('ozProcIzn'), dataIndex: 'procIzn', width: 90 },
          { title: t('ozPz'), dataIndex: 'pz', width: 70 },
          { title: t('ozPz41'), dataIndex: 'pz41', width: 70 },
          { title: t('ozPz62'), dataIndex: 'pz62', width: 70 },
        ]}
      />
    );
  }
  if (kind === 'ia31') {
    const rows = sample.map(([godMes, v]) => ({ godMes, ...v }));
    return (
      <Table
        size="small"
        rowKey="godMes"
        pagination={false}
        dataSource={rows}
        columns={[
          { title: t('ozGodMes'), dataIndex: 'godMes', width: 100 },
          { title: t('ozIa31Hours'), dataIndex: 'sati' },
          { title: t('ozIa31PointValue'), dataIndex: 'bodIznos' },
          { title: t('ozIa31MaxBase'), dataIndex: 'maxOsnovica' },
          { title: t('ozIa31MinBase'), dataIndex: 'minOsnovica' },
        ]}
      />
    );
  }
  if (kind === 'ia43') {
    return (
      <Table
        size="small"
        rowKey={(_, i) => i}
        pagination={false}
        dataSource={sample}
        columns={[
          { title: t('ozMaticniBroj'), dataIndex: 'maticniBroj' },
          { title: t('jmbg'), dataIndex: 'jmbg' },
          { title: t('name'), dataIndex: 'name', render: (v) => v || '—' },
          {
            title: t('ozContractAvgGross'),
            dataIndex: 'contractAvgGross',
            render: (v) => v?.toLocaleString('sr-RS'),
          },
          {
            title: t('ozImportMatched'),
            dataIndex: 'matched',
            render: (v) =>
              v ? <Tag color="green">✓</Tag> : <Tag color="orange">?</Tag>,
          },
        ]}
      />
    );
  }
  if (kind === 'ia43-targets') {
    return (
      <Table
        size="small"
        rowKey={(_, i) => i}
        pagination={false}
        dataSource={sample}
        columns={[
          { title: t('ozMaticniBroj'), dataIndex: 'matic' },
          { title: t('name'), dataIndex: 'name', render: (v) => v || '—' },
          { title: t('ozImportVrsta'), dataIndex: 'vrsta', width: 70 },
          { title: t('ozImportIsplata'), dataIndex: 'isplata', width: 80 },
          { title: t('ozImportBuckets'), dataIndex: 'buckets', width: 80 },
          {
            title: t('ozImportMatched'),
            dataIndex: 'matched',
            render: (v) =>
              v ? <Tag color="green">✓</Tag> : <Tag color="orange">?</Tag>,
          },
        ]}
      />
    );
  }
  if (kind === 'long-sick') {
    return (
      <Table
        size="small"
        rowKey={(_, i) => i}
        pagination={false}
        dataSource={sample}
        columns={[
          { title: t('ozMaticniBroj'), dataIndex: 'matic' },
          { title: t('name'), dataIndex: 'name', render: (v) => v || '—' },
          { title: t('ozImportMonths'), dataIndex: 'months', width: 90 },
          {
            title: t('ozImportSample'),
            dataIndex: 'sample',
            render: (v) =>
              (v || [])
                .map(([gm, amt]) => `${gm}: ${amt?.toLocaleString('sr-RS')}`)
                .join('  •  '),
          },
          {
            title: t('ozImportMatched'),
            dataIndex: 'matched',
            render: (v) =>
              v ? <Tag color="green">✓</Tag> : <Tag color="orange">?</Tag>,
          },
        ]}
      />
    );
  }
  if (kind === 'ia42') {
    return (
      <Table
        size="small"
        rowKey={(_, i) => i}
        pagination={false}
        dataSource={sample}
        columns={[
          { title: t('ozMaticniBroj'), dataIndex: 'maticniBroj' },
          { title: t('firstName'), dataIndex: 'firstName' },
          { title: t('lastName'), dataIndex: 'lastName' },
          { title: t('ozSectorCode'), dataIndex: 'sectorCode' },
          { title: t('ozWorkTimePercent'), dataIndex: 'workTimePercent' },
          {
            title: t('ozImportMatched'),
            dataIndex: 'matched',
            render: (v) =>
              v ? <Tag color="green">✓</Tag> : <Tag color="orange">?</Tag>,
          },
        ]}
      />
    );
  }
  return null;
}

export default function OzAbSoftImport({ companyId }) {
  const { t } = useTranslation(['forms']);

  if (!companyId) {
    return (
      <div className="p-8 text-center text-gray-400">{t('selectCompany')}</div>
    );
  }

  return (
    <div>
      <h3 className="mb-3 font-semibold">{t('ozImportAbSoft')}</h3>
      <Tabs
        size="small"
        items={KINDS.map((k) => ({
          key: k.key,
          label: t(k.tKey),
          children: <ImportPanel companyId={companyId} kind={k.key} t={t} />,
        }))}
      />
    </div>
  );
}
